'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { loadProductionState, saveProductionState, type ProductionShift } from '@/features/kitchen/storage/productionStorage'
import {
  fetchTodayProductionPlan,
  fetchTodayProductionSessions,
  shouldUseProductionApi,
  type ApiProductionSession,
  type ApiTodayPlan,
} from '@/features/kitchen/api/getTodayProduction'
import { Link } from '@/shared/navigation'
import { postProductionSession } from '@/features/kitchen/api/postProduction'
import { downloadTodayProductionPlanPdf } from '@/features/kitchen/api/downloadTodayProductionPlanPdf'
import { fetchKitchenStock } from '@/features/kitchen/api/getDispatchOptions'
import { fetchTodayDeliveries, type ApiDelivery } from '@/features/kitchen/api/getTodayDispatch'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { buildPlanCodeFallback, buildProductionRefPrefix } from '@/features/kitchen/productionCodes'
import { PlanProductionTable } from '@/features/kitchen/PlanProductionTable'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import {
  computePlanLineRows,
  groupSessionLinesByCategory,
  formatNeedCell,
  needCellClass,
  productionNeed,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'
import type { ProductionLineRecord } from '@/features/kitchen/storage/productionStorage'

type SessionStatus = 'in_progress' | 'draft' | 'posted'

type ProductionLine = ProductionLineRecord

type ProductionSession = {
  ref: string
  title: string
  updatedAt: string
  status: SessionStatus
  shift?: ProductionShift
  note?: string
}

type TodayPlanDetail = {
  planCode: string
  notes?: string
  catalog: PlanCatalogItem[]
}

const DEFAULT_PLAN_CATALOG: PlanCatalogItem[] = [
  { sku: 'Roti coklat', plan: 100, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Roti keju', plan: 80, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat gula', plan: 50, categoryName: 'Roti Manis', categorySortOrder: 1 },
]

const PRODUCTION_FLOW_STEPS = [
  { label: 'Isi hasil' },
  { label: 'Konfirmasi' },
  { label: 'Masuk stok' },
] as const

const PRODUCTION_JOURNEY_STEPS = [
  { label: 'Plan HO' },
  { label: 'Sesi' },
  { label: 'Masuk stok' },
] as const

const DEFAULT_SESSION_SHIFT: ProductionShift = 'MORNING'

function buildMergedCatalog(plan: ApiTodayPlan): PlanCatalogItem[] {
  const categoryByProduct = new Map<string, { name: string; sort: number }>()
  for (const item of plan.plans ?? []) {
    for (const line of item.lines) {
      const key = line.product_name.trim().toLowerCase()
      const cat = line.category_name?.trim()
      if (cat) {
        categoryByProduct.set(key, { name: cat, sort: line.category_sort_order ?? 9999 })
      }
    }
  }
  return (plan.merged_lines ?? []).map((line) => {
    const key = line.product_name.trim().toLowerCase()
    const fromPlan = categoryByProduct.get(key)
    const categoryName = line.category_name?.trim() || fromPlan?.name || 'Lainnya'
    return {
      sku: line.product_name,
      plan: line.target_qty,
      categoryName,
      categorySortOrder: line.category_sort_order ?? fromPlan?.sort ?? 9999,
    }
  })
}

function onlyPlanLines(lines: ProductionLine[]): ProductionLine[] {
  return lines.filter((line) => line.source === 'plan')
}

function formatSessionDateLabel(dateIso: string): string {
  const date = new Date(`${dateIso}T12:00:00`)
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date)
}

function buildSessionTitle(dateIso: string): string {
  return formatSessionDateLabel(dateIso)
}

function productionJourneyActiveStep(
  view: 'plan' | 'sessions',
  selectedSessionRef: string | null,
  editorStep: 'edit' | 'review',
): number {
  if (selectedSessionRef && editorStep === 'review') return 3
  if (view === 'sessions') return 2
  return 1
}

function productionFlowActiveStep(
  status: SessionStatus,
  editorStep: 'edit' | 'review',
): number {
  if (status === 'posted') return PRODUCTION_FLOW_STEPS.length + 1
  if (editorStep === 'review') return 2
  return 1
}

const DEFAULT_GOOD_STOCK: Record<string, number> = {
  'Roti coklat': 100,
  'Roti keju': 60,
  'Donat gula': 0,
}

function dispatchedQtyFromDeliveries(deliveries: ApiDelivery[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const delivery of deliveries) {
    if (delivery.status === 'prepared' || delivery.status === 'draft') continue
    for (const line of delivery.lines) {
      if (line.qty <= 0 || !line.product_name.trim()) continue
      const name = line.product_name.trim()
      out[name] = (out[name] ?? 0) + line.qty
    }
  }
  return out
}

function qtyForProduct(map: Record<string, number>, productName: string): number {
  const exact = map[productName]
  if (exact !== undefined) return exact
  const lower = productName.trim().toLowerCase()
  for (const [name, qty] of Object.entries(map)) {
    if (name.toLowerCase() === lower) return qty
  }
  return 0
}

function getActualInputHint(
  actual: number,
  need: number,
): { text: string; className: string } {
  if (actual === 0) {
    if (need <= 0) {
      return { text: 'Stok sudah cukup untuk rencana', className: 'text-on-surface-variant' }
    }
    return { text: `Perlu produksi ${need} pcs`, className: 'text-on-surface-variant' }
  }
  if (need <= 0) {
    return { text: 'Input di luar kebutuhan (stok cukup)', className: 'text-status-success' }
  }
  const diff = actual - need
  if (diff === 0) return { text: 'Kebutuhan terpenuhi', className: 'text-status-success' }
  if (diff > 0) {
    return { text: `Lebih ${diff} pcs dari kebutuhan`, className: 'text-status-success' }
  }
  return { text: `Kurang ${Math.abs(diff)} pcs dari kebutuhan`, className: 'text-status-warning' }
}

function stockQtyForProduct(stockByProduct: Record<string, number>, productName: string): number {
  return qtyForProduct(stockByProduct, productName)
}

function nextSessionRef(date: string, sessions: ProductionSession[], kitchenCode: string): string {
  const prefix = buildProductionRefPrefix(date, kitchenCode)
  let maxSeq = 0

  for (const session of sessions) {
    if (!session.ref.startsWith(prefix)) continue
    const seq = Number.parseInt(session.ref.slice(prefix.length), 10)
    if (!Number.isNaN(seq)) maxSeq = Math.max(maxSeq, seq)
  }

  return `${prefix}${String(maxSeq + 1).padStart(2, '0')}`
}

function isPosted(status: SessionStatus): boolean {
  return status === 'posted'
}

function createLinesFromPlan(planCatalog: PlanCatalogItem[]): ProductionLine[] {
  return planCatalog.map((item, index) => ({
    id: `p-${Date.now()}-${index}`,
    productName: item.sku,
    source: 'plan' as const,
    planQty: item.plan,
    qty: 0,
  }))
}

function formatUpdatedAt(): string {
  return `Diperbarui ${new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date())}`
}

function formatPostedAt(iso?: string): string {
  const date = iso ? new Date(iso) : new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `Sudah masuk stok ${day} ${time}`
}

function getFilledLines(lines: ProductionLine[]): ProductionLine[] {
  return lines.filter((line) => line.qty > 0 && line.productName.trim())
}

function formatSessionUpdatedAt(iso: string, status: SessionStatus): string {
  if (status === 'posted') return formatPostedAt(iso)
  try {
    return `Diperbarui ${new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))}`
  } catch {
    return 'Diperbarui'
  }
}

function mapApiSession(session: ApiProductionSession, planDate: string): ProductionSession {
  const shift = (['MORNING', 'NOON', 'EVENING'].includes(session.session_code)
    ? session.session_code
    : 'MORNING') as ProductionShift
  return {
    ref: session.ref,
    title: buildSessionTitle(planDate),
    shift,
    updatedAt: formatSessionUpdatedAt(session.updated_at, session.status),
    status: session.status,
    note: session.note,
  }
}

function mapApiSessionLines(lines: ApiProductionSession['lines']): ProductionLine[] {
  return lines.map((line, index) => ({
    id: `api-${index}-${line.product_name}`,
    productName: line.product_name,
    source: line.source,
    planQty: line.plan_qty,
    qty: line.qty,
  }))
}

export function KitchenProductionPage() {
  const useApi = shouldUseProductionApi()
  const initialProduction = useApi
    ? { sessions: [] as ProductionSession[], sessionLinesByRef: {} as Record<string, ProductionLine[]> }
    : loadProductionState()
  const [view, setView] = useState<'plan' | 'sessions'>('plan')
  const [sessionFilter, setSessionFilter] = useState<'todo' | 'history'>('todo')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [sessionDate, setSessionDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [createError, setCreateError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [selectedSessionRef, setSelectedSessionRef] = useState<string | null>(null)
  const [editorStep, setEditorStep] = useState<'edit' | 'review'>('edit')
  const [isPinModalOpen, setIsPinModalOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [sessionLinesByRef, setSessionLinesByRef] = useState<Record<string, ProductionLine[]>>(
    initialProduction.sessionLinesByRef,
  )
  const [editorNote, setEditorNote] = useState('')

  const device = getKitchenDevice()
  const kitchenName = device?.kitchenName ?? 'Dapur'
  const kitchenCode = device?.kitchenCode ?? 'DAPUR-A'

  const todayFull = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const [planDetails, setPlanDetails] = useState<TodayPlanDetail[]>([])
  const [showPlanDetails, setShowPlanDetails] = useState(false)
  const [apiLoadError, setApiLoadError] = useState<string | null>(null)
  const [isLoadingPlan, setIsLoadingPlan] = useState(useApi)
  const [sessions, setSessions] = useState<ProductionSession[]>(initialProduction.sessions)
  const [stockByProduct, setStockByProduct] = useState<Record<string, number>>(DEFAULT_GOOD_STOCK)
  const [dispatchedByProduct, setDispatchedByProduct] = useState<Record<string, number>>({})

  const [mergedCatalog, setMergedCatalog] = useState<PlanCatalogItem[]>(DEFAULT_PLAN_CATALOG)
  const [planLabel, setPlanLabel] = useState(() =>
    buildPlanCodeFallback(new Date().toISOString().slice(0, 10), kitchenCode),
  )
  const planCatalog = mergedCatalog
  const planCode = planLabel
  const planStatus = mergedCatalog.length > 0 ? 'published' : 'none'

  const applyProductionPayload = useCallback(
    (
      plan: Awaited<ReturnType<typeof fetchTodayProductionPlan>>,
      sessionPayload: Awaited<ReturnType<typeof fetchTodayProductionSessions>>,
      stockItems: Awaited<ReturnType<typeof fetchKitchenStock>>,
      deliveryPayload: Awaited<ReturnType<typeof fetchTodayDeliveries>>,
    ) => {
      const stockMap: Record<string, number> = {}
      for (const item of stockItems) {
        stockMap[item.productName] = item.stockAvailable
      }
      setStockByProduct(stockMap)
      setDispatchedByProduct(dispatchedQtyFromDeliveries(deliveryPayload.deliveries ?? []))

      const merged = buildMergedCatalog(plan)
      setMergedCatalog(merged)
      setPlanDetails(
        (plan.plans ?? []).map((item) => ({
          planCode: item.plan_code,
          notes: item.notes,
          catalog: item.lines.map((line) => ({
            sku: line.product_name,
            plan: line.target_qty,
            categoryName: line.category_name?.trim() || 'Lainnya',
            categorySortOrder: line.category_sort_order ?? 9999,
          })),
        })),
      )
      setPlanLabel(
        plan.plans.length === 1
          ? plan.plans[0].plan_code
          : plan.plans.length > 1
            ? `${plan.plans.length} plan HO · ${kitchenName}`
            : buildPlanCodeFallback(plan.plan_date || new Date().toISOString().slice(0, 10), kitchenCode),
      )

      const planDate = plan.plan_date || new Date().toISOString().slice(0, 10)
      const catalogForLines = merged.length > 0 ? merged : DEFAULT_PLAN_CATALOG
      const nextSessions = sessionPayload.sessions.map((session) => mapApiSession(session, planDate))
      const nextLines: Record<string, ProductionLine[]> = {}
      for (const session of sessionPayload.sessions) {
        if (session.lines.length > 0) {
          nextLines[session.ref] = mapApiSessionLines(session.lines)
        } else if (session.status !== 'posted' && catalogForLines.length > 0) {
          nextLines[session.ref] = createLinesFromPlan(catalogForLines)
        } else {
          nextLines[session.ref] = []
        }
      }
      setSessions(nextSessions)
      setSessionLinesByRef(nextLines)
    },
    [kitchenCode, kitchenName],
  )

  const reloadProductionData = useCallback(
    async (options?: { showLoading?: boolean }) => {
      if (!useApi || !device?.deviceId) return

      if (options?.showLoading !== false) {
        setIsLoadingPlan(true)
      }
      setApiLoadError(null)

      try {
        const [plan, sessionPayload, stockItems, deliveryPayload] = await Promise.all([
          fetchTodayProductionPlan(device.deviceId),
          fetchTodayProductionSessions(device.deviceId),
          fetchKitchenStock(device.deviceId),
          fetchTodayDeliveries(device.deviceId),
        ])
        applyProductionPayload(plan, sessionPayload, stockItems, deliveryPayload)
      } catch (err) {
        setApiLoadError(err instanceof Error ? err.message : 'Gagal memuat data produksi.')
      } finally {
        if (options?.showLoading !== false) {
          setIsLoadingPlan(false)
        }
      }
    },
    [useApi, device?.deviceId, applyProductionPayload],
  )

  useEffect(() => {
    if (!useApi) {
      saveProductionState({ sessions, sessionLinesByRef })
    }
  }, [sessions, sessionLinesByRef, useApi])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    setIsLoadingPlan(true)
    setApiLoadError(null)

    Promise.all([
      fetchTodayProductionPlan(device.deviceId),
      fetchTodayProductionSessions(device.deviceId),
      fetchKitchenStock(device.deviceId),
      fetchTodayDeliveries(device.deviceId),
    ])
      .then(([plan, sessionPayload, stockItems, deliveryPayload]) => {
        if (cancelled) return
        applyProductionPayload(plan, sessionPayload, stockItems, deliveryPayload)
      })
      .catch((err) => {
        if (cancelled) return
        setApiLoadError(err instanceof Error ? err.message : 'Gagal memuat data produksi.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPlan(false)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, applyProductionPayload])

  const planLines = useMemo(
    () =>
      computePlanLineRows(
        sessions,
        sessionLinesByRef,
        planCatalog,
        stockByProduct,
        dispatchedByProduct,
        qtyForProduct,
        stockQtyForProduct,
      ),
    [sessions, sessionLinesByRef, planCatalog, stockByProduct, dispatchedByProduct],
  )

  const planPct = useMemo(() => {
    const totalNeed = planLines.reduce((sum, line) => sum + Math.max(0, line.need), 0)
    const totalActual = planLines.reduce((sum, line) => sum + line.totalActual, 0)
    if (totalNeed <= 0) return totalActual > 0 ? 100 : 0
    return Math.min(100, Math.round((totalActual / totalNeed) * 100))
  }, [planLines])

  const sessionLineGroups = useMemo(() => {
    if (!selectedSessionRef) return []
    const lines = onlyPlanLines(sessionLinesByRef[selectedSessionRef] ?? [])
    return groupSessionLinesByCategory(lines, planCatalog)
  }, [selectedSessionRef, sessionLinesByRef, planCatalog])

  const [isDownloadingPlan, setIsDownloadingPlan] = useState(false)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const todoSessions = useMemo(() => sessions.filter((s) => !isPosted(s.status)), [sessions])
  const historyCount = sessions.filter((s) => isPosted(s.status)).length
  const hasActiveTodo = todoSessions.length > 0

  async function handleDownloadPlanPdf() {
    if (!useApi) {
      showToast('Unduh PDF membutuhkan koneksi API.')
      return
    }
    setIsDownloadingPlan(true)
    try {
      await downloadTodayProductionPlanPdf()
      showToast('File PDF plan produksi berhasil diunduh.')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Gagal unduh PDF plan produksi.')
    } finally {
      setIsDownloadingPlan(false)
    }
  }

  function getSessionSummary(session: ProductionSession): string {
    const lines = sessionLinesByRef[session.ref] ?? []
    const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
    const skuCount = lines.length
    if (skuCount === 0) return 'Belum ada produk diisi'
    return `${skuCount} produk · ${totalQty} pcs`
  }

  function openCreateModal() {
    setSessionDate(new Date().toISOString().slice(0, 10))
    setCreateError(null)
    setIsCreateOpen(true)
  }

  function createNewSession(forDate?: string) {
    const date = forDate ?? sessionDate
    if (!date) {
      setCreateError('Tanggal produksi wajib diisi.')
      return
    }
    if (mergedCatalog.length === 0) {
      setCreateError('Belum ada plan HO untuk hari ini.')
      return
    }
    const ref = nextSessionRef(date, sessions, kitchenCode)

    const next: ProductionSession = {
      ref,
      title: buildSessionTitle(date),
      shift: DEFAULT_SESSION_SHIFT,
      updatedAt: 'Baru dibuat',
      status: 'draft',
    }

    setSessions((prev) => [next, ...prev])
    setSessionLinesByRef((prev) => ({
      ...prev,
      [ref]: createLinesFromPlan(mergedCatalog),
    }))
    setSelectedSessionRef(ref)
    setEditorNote('')
    setEditorStep('edit')
    setSaveError(null)
    setSessionFilter('todo')
    setView('sessions')
    setIsCreateOpen(false)
    showToast('Input produksi dibuat. Isi jumlah hasilnya.')
  }

  function startProductionToday() {
    const todayIso = new Date().toISOString().slice(0, 10)
    if (mergedCatalog.length === 0) {
      showToast('Belum ada plan HO untuk hari ini.')
      return
    }
    setSessionDate(todayIso)
    createNewSession(todayIso)
  }

  function canDeleteSession(session: ProductionSession): boolean {
    if (isPosted(session.status)) return false
    const lines = sessionLinesByRef[session.ref] ?? []
    return getFilledLines(lines).length === 0
  }

  function deleteDraftSession(ref: string) {
    const session = sessions.find((s) => s.ref === ref)
    if (!session || !canDeleteSession(session)) return
    setSessions((prev) => prev.filter((s) => s.ref !== ref))
    setSessionLinesByRef((prev) => {
      const next = { ...prev }
      delete next[ref]
      return next
    })
    showToast('Input kosong dihapus.')
  }

  function updateLine(selectedRef: string, lineId: string, patch: Partial<ProductionLine>) {
    const current = sessionLinesByRef[selectedRef] ?? []
    const next = current.map((line) => (line.id === lineId ? { ...line, ...patch } : line))
    setSessionLinesByRef((prev) => ({ ...prev, [selectedRef]: next }))
  }

  function selectSession(ref: string) {
    const session = sessions.find((s) => s.ref === ref)
    setSelectedSessionRef(ref)
    setEditorNote(session?.note ?? '')
    setEditorStep('edit')
    setSaveError(null)
  }

  function closeSession() {
    setSelectedSessionRef(null)
    setEditorStep('edit')
    setIsPinModalOpen(false)
    setSaveError(null)
  }

  function validateFilledLines(lines: ProductionLine[]): boolean {
    const hasQty = getFilledLines(lines).length > 0
    if (!hasQty) {
      setSaveError('Isi minimal 1 produk dengan jumlah lebih dari 0.')
      return false
    }
    setSaveError(null)
    return true
  }

  function persistSessionDraft(ref: string, note: string) {
    setSessions((prev) =>
      prev.map((session) =>
        session.ref === ref
          ? { ...session, status: session.status === 'posted' ? 'posted' : 'in_progress', note, updatedAt: formatUpdatedAt() }
          : session,
      ),
    )
  }

  function saveProductionResult() {
    if (!selectedSessionRef) return
    const lines = sessionLinesByRef[selectedSessionRef] ?? []
    if (!validateFilledLines(lines)) return

    persistSessionDraft(selectedSessionRef, editorNote)
    closeSession()
    setSessionFilter('todo')
    showToast('Hasil produksi disimpan.')
  }

  function openStockReview() {
    if (!selectedSessionRef) return
    const lines = sessionLinesByRef[selectedSessionRef] ?? []
    if (!validateFilledLines(lines)) return

    persistSessionDraft(selectedSessionRef, editorNote)
    setEditorStep('review')
  }

  async function confirmPostToStock(pin: string) {
    if (!selectedSessionRef || !device) {
      throw new Error('Perangkat tidak terdaftar. Aktivasi ulang diperlukan.')
    }

    const lines = getFilledLines(onlyPlanLines(sessionLinesByRef[selectedSessionRef] ?? []))
    const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
    const result = await postProductionSession({
      sessionRef: selectedSessionRef,
      deviceId: device.deviceId,
      pin,
      lines: lines.map((line) => ({
        productName: line.productName.trim(),
        qty: line.qty,
        source: line.source,
      })),
      note: editorNote.trim() || undefined,
    })

    const postedLabel = formatPostedAt(result.posted_at)
    setSessions((prev) =>
      prev.map((session) =>
        session.ref === selectedSessionRef
          ? { ...session, status: 'posted', note: editorNote, updatedAt: postedLabel }
          : session,
      ),
    )
    setIsPinModalOpen(false)
    closeSession()
    setSessionFilter('history')
    await reloadProductionData({ showLoading: false })
    showToast(
      `${totalQty} pcs sudah masuk stok.${consumeLastSyncQueued() ? ` ${kitchenTerms.sync.queued}` : ''}`,
    )
  }

  const orderedSessions = [...sessions].sort((a, b) => {
    const rank = { in_progress: 0, draft: 1, posted: 2 } as const
    return rank[a.status] - rank[b.status]
  })

  const visibleSessions = orderedSessions.filter((session) =>
    sessionFilter === 'todo' ? !isPosted(session.status) : isPosted(session.status),
  )

  const selectedSession = sessions.find((session) => session.ref === selectedSessionRef) ?? null
  const selectedLines = selectedSession
    ? onlyPlanLines(sessionLinesByRef[selectedSession.ref] ?? [])
    : []
  const filledLines = getFilledLines(selectedLines)
  const filledTotalQty = filledLines.reduce((sum, line) => sum + line.qty, 0)

  return (
    <section className="space-y-4">
      <div className="card">
        <p className="text-headline-md text-primary">Input produksi hari ini</p>
        <p className="text-body-sm text-on-surface-variant mt-1">
          Produksi = Plan − Stok − terkirim hari ini. Rincian aktual per sesi tersedia di PDF.
        </p>
        <div className="mt-3 pt-3 border-t border-surface-variant">
          <FlowStepper
            steps={[...PRODUCTION_JOURNEY_STEPS]}
            activeStep={productionJourneyActiveStep(view, selectedSessionRef, editorStep)}
          />
        </div>
      </div>

      <div className="card p-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              setView('plan')
              closeSession()
              void reloadProductionData({ showLoading: false })
            }}
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
              view === 'plan' ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-variant bg-surface'
            }`}
          >
            Plan HO
          </button>
          <button
            type="button"
            onClick={() => {
              setView('sessions')
              setSessionFilter('todo')
            }}
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
              view === 'sessions'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
          >
            Sesi produksi
          </button>
        </div>
      </div>

      {view === 'plan' && (
        <div className="card space-y-3">
          <div>
            <p className="font-label-md font-mono text-on-surface-variant">{planCode}</p>
            <p className="text-headline-md text-primary mt-1">Plan produksi hari ini</p>
            <p className="text-body-sm text-on-surface-variant mt-1 capitalize">
              {kitchenName} · {todayFull}
            </p>
            <p className="text-label-md text-on-surface-variant mt-1">
              Target gabungan semua plan HO · status {planStatus}
            </p>
            {planDetails.length > 1 && (
              <button
                type="button"
                onClick={() => setShowPlanDetails((prev) => !prev)}
                className="text-body-sm text-primary font-semibold mt-2 underline-offset-2 hover:underline"
              >
                {showPlanDetails ? 'Sembunyikan detail plan' : `Lihat ${planDetails.length} plan dari HO`}
              </button>
            )}
          </div>

          {showPlanDetails && planDetails.length > 0 && (
            <div className="space-y-2 border border-outline-variant rounded-lg p-3 bg-surface-container">
              {planDetails.map((detail) => (
                <div key={detail.planCode} className="text-body-sm">
                  <p className="font-mono font-semibold text-on-surface">{detail.planCode}</p>
                  {detail.notes && (
                    <p className="text-on-surface-variant mt-0.5">{detail.notes}</p>
                  )}
                  <p className="text-label-md text-on-surface-variant mt-1">
                    {detail.catalog.map((line) => `${line.sku} ${line.plan}`).join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {apiLoadError && (
            <div role="alert" className="alert-error">
              <span>{apiLoadError}</span>
            </div>
          )}

          {isLoadingPlan ? (
            <SkeletonList count={3} label="Memuat plan dari server" />
          ) : planCatalog.length === 0 ? (
            <EmptyState
              icon="event_busy"
              title="Belum ada plan HO"
              description="Plan produksi dari Head Office belum tersedia untuk hari ini."
            />
          ) : (
          <>
          <div>
            <div className="flex items-center justify-between text-body-sm mb-1">
              <span className="text-on-surface-variant">Progress actual vs kebutuhan</span>
              <span className="font-semibold text-primary">{planPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-surface-variant overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${planPct}%` }}
              />
            </div>
          </div>

          <PlanProductionTable planLines={planLines} />

          <p className="text-body-sm text-on-surface-variant">
            Stok = good stock dapur. Produksi = kebutuhan tersisa setelah stok & kiriman hari ini. Aktual = total input yang sudah disimpan (per sesi di PDF).
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleDownloadPlanPdf}
              disabled={isDownloadingPlan || planStatus === 'none'}
              className="btn-outline w-full sm:flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {isDownloadingPlan ? 'Mengunduh…' : 'Unduh PDF'}
            </button>
            {hasActiveTodo ? (
              <button
                type="button"
                onClick={() => {
                  setView('sessions')
                  setSessionFilter('todo')
                }}
                className="btn-primary w-full sm:flex-1"
              >
                Lanjut input produksi
              </button>
            ) : (
              <button type="button" onClick={startProductionToday} className="btn-primary w-full sm:flex-1">
                Mulai input hari ini
              </button>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {view === 'sessions' && (
        <div className="space-y-3">
          {!selectedSession && (
            <>
              <div className="card space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-label-md font-mono text-on-surface-variant">Plan: {planCode}</p>
                    <p className="text-body-sm text-on-surface-variant mt-1">
                      Buat sesi kapan saja sesuai kebutuhan dapur.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadPlanPdf}
                      disabled={isDownloadingPlan || planStatus === 'none'}
                      className="btn-outline inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <span className="material-symbols-outlined text-lg">download</span>
                      {isDownloadingPlan ? 'Mengunduh…' : 'Unduh PDF'}
                    </button>
                    <button type="button" className="btn-primary" onClick={startProductionToday}>
                      Mulai input hari ini
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="text-body-sm text-primary font-semibold underline-offset-2 hover:underline"
                >
                  Atau pilih tanggal lain
                </button>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'todo', label: 'Belum masuk stok', count: todoSessions.length },
                    { key: 'history', label: 'Sudah masuk stok', count: historyCount },
                  ].map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setSessionFilter(filter.key as typeof sessionFilter)}
                      className={`rounded-lg px-3 py-2 text-body-sm font-semibold ${
                        sessionFilter === filter.key
                          ? 'bg-primary-container text-on-primary-container'
                          : 'bg-surface text-on-surface-variant border border-outline-variant'
                      }`}
                    >
                      {filter.label}
                      {filter.count > 0 && (
                        <span className="ml-1 opacity-80">({filter.count})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {visibleSessions.map((session) => {
                const posted = isPosted(session.status)
                const deletable = canDeleteSession(session)
                return (
                  <div key={session.ref} className="card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-label-md font-mono text-on-surface-variant">{session.ref}</p>
                        <p className="text-body-lg font-bold text-on-surface mt-1">{session.title}</p>
                        <p className="text-body-sm text-on-surface-variant mt-1">{getSessionSummary(session)}</p>
                        <p className="text-label-md text-on-surface-variant mt-2">{session.updatedAt}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {posted && <span className="badge-success">Sudah masuk stok</span>}
                        {deletable && (
                          <button
                            type="button"
                            onClick={() => deleteDraftSession(session.ref)}
                            className="text-status-danger p-1"
                            aria-label="Hapus input kosong"
                          >
                            <span className="material-symbols-outlined text-xl">delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectSession(session.ref)}
                      className={`mt-3 w-full min-h-[44px] ${posted ? 'btn-outline' : 'btn-primary'}`}
                    >
                      {posted ? 'Lihat detail' : 'Lanjut isi'}
                    </button>
                  </div>
                )
              })}
              {visibleSessions.length === 0 && (
                <div className="card text-center space-y-3 text-body-sm text-on-surface-variant">
                  <p>
                    {sessionFilter === 'todo'
                      ? 'Belum ada input produksi hari ini.'
                      : 'Belum ada produksi yang sudah masuk stok.'}
                  </p>
                  {sessionFilter === 'todo' && (
                    <button type="button" className="btn-primary w-full sm:w-auto" onClick={startProductionToday}>
                      Mulai input hari ini
                    </button>
                  )}
                </div>
              )}
              <Link href="/kitchen/home" className="btn-outline block text-center">
                Kembali ke beranda
              </Link>
            </>
          )}

          {selectedSession && editorStep === 'edit' && (
            <div className={!isPosted(selectedSession.status) ? 'relative pb-28' : undefined}>
              <div className="card space-y-3">
                <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
                  <div>
                    <p className="font-label-md font-mono text-on-surface-variant">{selectedSession.ref}</p>
                    <p className="text-headline-md text-primary mt-1">Isi hasil</p>
                    <p className="text-body-sm text-on-surface-variant">{selectedSession.title}</p>
                  </div>
                  <button type="button" className="btn-outline min-h-[44px]" onClick={closeSession}>
                    Kembali
                  </button>
                </div>

                <FlowStepper
                  steps={[...PRODUCTION_FLOW_STEPS]}
                  activeStep={productionFlowActiveStep(selectedSession.status, editorStep)}
                />

              {isPosted(selectedSession.status) && filledLines.length > 0 && (
                <div className="rounded-lg bg-surface-container border border-outline-variant p-3 space-y-2">
                  <p className="text-body-sm font-semibold text-on-surface">Ringkasan vs plan HO</p>
                  {filledLines.map((line) => {
                    const planItem = planCatalog.find((item) => item.sku === line.productName)
                    if (!planItem) return null
                    const stock = stockQtyForProduct(stockByProduct, line.productName)
                    const dispatched = qtyForProduct(dispatchedByProduct, line.productName)
                    const need = productionNeed(planItem.plan, stock, dispatched)
                    const hint = getActualInputHint(line.qty, need)
                    return (
                      <div key={line.id} className="flex items-center justify-between gap-2 text-body-sm">
                        <span className="text-on-surface">{line.productName}</span>
                        <span className="text-on-surface-variant">
                          {line.qty} pcs · <span className={hint.className}>{hint.text}</span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="space-y-2">
                {sessionLineGroups.map((group) => (
                  <CollapsibleCategoryBlock
                    key={group.categoryName}
                    categoryName={group.categoryName}
                    itemCount={group.items.length}
                    defaultOpen={false}
                  >
                    <div className="space-y-2 p-2">
                      {group.items.map((line) => {
                        const planItem = planCatalog.find((item) => item.sku === line.productName)
                        const stock = stockQtyForProduct(stockByProduct, line.productName)
                        const dispatched = qtyForProduct(dispatchedByProduct, line.productName)
                        const planQty = line.planQty ?? planItem?.plan ?? null
                        const need = planQty !== null ? productionNeed(planQty, stock, dispatched) : 0
                        const inputHint = getActualInputHint(line.qty, need)
                        return (
                          <div key={line.id} className="rounded-lg border border-outline-variant p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-body-lg font-semibold text-on-surface flex-1">{line.productName}</p>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-center text-label-md">
                              <div>
                                <p className="text-on-surface-variant">Stok</p>
                                <p className="font-semibold text-on-surface">{stock}</p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">Plan</p>
                                <p className="font-semibold text-on-surface">{planQty === null ? '—' : planQty}</p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">Produksi</p>
                                <p
                                  className={`font-semibold ${planQty === null ? 'text-on-surface' : needCellClass(need)}`}
                                >
                                  {planQty === null ? '—' : formatNeedCell(need)}
                                </p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">Aktual</p>
                                <p className="font-semibold text-on-surface">{line.qty}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <label className="text-body-sm text-on-surface-variant">Jumlah</label>
                              <QtyStepper
                                value={line.qty}
                                min={0}
                                onChange={(qty) =>
                                  selectedSessionRef && updateLine(selectedSessionRef, line.id, { qty })
                                }
                                disabled={isPosted(selectedSession.status)}
                              />
                              <span className="text-body-sm text-on-surface-variant">pcs</span>
                            </div>
                            {!isPosted(selectedSession.status) && (
                              <p className={`text-label-md font-medium ${inputHint.className}`}>{inputHint.text}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </CollapsibleCategoryBlock>
                ))}
              </div>

              <div className="space-y-2">
                <label className="field-label" htmlFor="editor_note">
                  Catatan (opsional)
                </label>
                <textarea
                  id="editor_note"
                  rows={2}
                  value={editorNote}
                  onChange={(e) => setEditorNote(e.target.value)}
                  placeholder="Contoh: Oven 2 delay 15 menit"
                  disabled={isPosted(selectedSession.status)}
                  className="input-field resize-none"
                />
              </div>

              {saveError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{saveError}</span>
                </div>
              )}

              {isPosted(selectedSession.status) && (
                <p className="text-body-sm text-on-surface-variant text-center">
                  Input ini sudah masuk stok dan tidak bisa diubah.
                </p>
              )}
              </div>

              {!isPosted(selectedSession.status) && (
                <StickyActionFooter>
                  <button type="button" className="btn-outline w-full min-h-[44px]" onClick={saveProductionResult}>
                    Simpan hasil produksi
                  </button>
                  <button type="button" className="btn-primary w-full min-h-[44px]" onClick={openStockReview}>
                    Masukkan ke stok
                  </button>
                  <p className="text-label-md text-on-surface-variant text-center">
                    Simpan dulu kalau belum selesai. Masuk stok = final, tidak bisa diubah.
                  </p>
                </StickyActionFooter>
              )}
            </div>
          )}

          {selectedSession && editorStep === 'review' && (
            <div className="relative pb-28">
              <div className="card space-y-3">
                <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
                  <div>
                    <p className="font-label-md font-mono text-on-surface-variant">{selectedSession.ref}</p>
                    <p className="text-headline-md text-primary mt-1">Konfirmasi masuk stok</p>
                    <p className="text-body-sm text-on-surface-variant">{selectedSession.title}</p>
                  </div>
                  <button type="button" className="btn-outline min-h-[44px]" onClick={() => setEditorStep('edit')}>
                    Kembali
                  </button>
                </div>

                <FlowStepper
                  steps={[...PRODUCTION_FLOW_STEPS]}
                  activeStep={productionFlowActiveStep(selectedSession.status, editorStep)}
                />

                <p className="text-body-sm text-on-surface-variant">
                  Periksa hasil produksi sebelum masuk stok. Setelah dikonfirmasi, data tidak bisa diubah.
                </p>

                <ActionSummaryCard
                  items={filledLines.map((line) => ({
                    id: line.id,
                    title: line.productName,
                    value: `${line.qty} pcs`,
                  }))}
                  totalLabel="Total"
                  totalValue={`${filledLines.length} produk · ${filledTotalQty} pcs`}
                />

                {editorNote.trim() && (
                  <div className="rounded-lg border border-outline-variant p-3">
                    <p className="text-label-md text-on-surface-variant">Catatan</p>
                    <p className="text-body-sm text-on-surface mt-1">{editorNote}</p>
                  </div>
                )}
              </div>

              <StickyActionFooter>
                <button
                  type="button"
                  className="btn-primary w-full min-h-[44px]"
                  onClick={() => setIsPinModalOpen(true)}
                >
                  Konfirmasi dengan PIN
                </button>
              </StickyActionFooter>
            </div>
          )}
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={dismissToast} />}

      <PinKeypadModal
        open={isPinModalOpen}
        title="PIN konfirmasi"
        description="Masukkan PIN 6 digit untuk memasukkan hasil produksi ke stok."
        confirmLabel="Konfirmasi masuk stok"
        onClose={() => setIsPinModalOpen(false)}
        onConfirm={confirmPostToStock}
      />

      {isCreateOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-surface-card border border-outline-variant rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex items-center justify-between">
              <p className="text-headline-md text-primary">Mulai input produksi</p>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="text-on-surface-variant"
                aria-label="Tutup"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-body-sm text-on-surface-variant">
                Produk dari rencana gabungan HO akan langsung muncul. Kamu tinggal isi jumlah hasil produksi.
              </p>
              <div className="space-y-2">
                <label className="field-label" htmlFor="session_date">
                  Tanggal produksi
                </label>
                <input
                  id="session_date"
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  className="input-field"
                />
                <p className="text-label-md text-on-surface-variant">
                  Judul sesi: {buildSessionTitle(sessionDate || new Date().toISOString().slice(0, 10))}
                </p>
              </div>
              {createError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{createError}</span>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 justify-end">
              <button type="button" className="btn-outline" onClick={() => setIsCreateOpen(false)}>
                Batal
              </button>
              <button type="button" className="btn-primary" onClick={() => createNewSession()}>
                Mulai
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  )
}
